import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, API, formatApiError, getAccessToken } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Play, Download, ArrowLeft, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 25b — Run an export. Reachable from:
 *   /app/admin/exports/run?preset=adp    (preset run)
 *   /app/admin/exports/run?profile=...   (saved profile run)
 * Prompts for date range (required), then kicks off /tenant/exports/run
 * and subscribes to SSE progress. Shows preflight errors when preset.
 */
export default function ExportRunPage() {
  const [params] = useSearchParams();
  const preset = params.get("preset");
  const profileId = params.get("profile");
  const navigate = useNavigate();

  const [preset_meta, setPresetMeta] = useState(null);
  const [profile_meta, setProfileMeta] = useState(null);
  const [dates, setDates] = useState({ from: firstOfMonth(), to: today() });
  const [job, setJob] = useState(null);
  const [progress, setProgress] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (preset) {
      api.get("/tenant/exports/presets").then(({ data }) => {
        setPresetMeta((data.items || []).find((p) => p.key === preset));
      });
    }
    if (profileId) {
      api.get(`/tenant/export-profiles/${profileId}`).then(({ data }) => {
        setProfileMeta(data);
        if (data.filters?.from) setDates({ from: data.filters.from, to: data.filters.to });
      });
    }
  }, [preset, profileId]);

  const run = async () => {
    setStarting(true);
    setJob(null); setProgress(null);
    try {
      const body = profileId
        ? { profile_id: profileId }
        : {
            preset,
            filters: { from: dates.from, to: dates.to, statuses: ["approved"] },
            filetype: "csv",
          };
      const { data } = await api.post("/tenant/exports/run", body);
      const jobId = data.job_id;
      // Subscribe to SSE
      const token = getAccessToken();
      const url = `${API}/tenant/exports/${jobId}/progress?token=${encodeURIComponent(token || "")}`;
      const es = new EventSource(url);
      es.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          setProgress(d);
          if (["ready", "failed", "failed_timeout", "too_many_rows_split_filters"].includes(d.status)) {
            es.close();
            refresh(jobId);
          }
        } catch (_e) { /* ignore */ }
      };
      es.addEventListener("done", () => { es.close(); refresh(jobId); });
      es.onerror = () => { es.close(); pollUntilDone(jobId); };
    } catch (e) {
      setStarting(false);
      toast.error(formatApiError(e));
    }
  };

  const refresh = async (jobId) => {
    try {
      const { data } = await api.get(`/tenant/exports/${jobId}`);
      setJob(data);
      setStarting(false);
    } catch (e) { setStarting(false); }
  };
  const pollUntilDone = async (jobId) => {
    for (let i = 0; i < 240; i++) {
      const { data } = await api.get(`/tenant/exports/${jobId}`);
      setJob(data);
      if (["ready", "failed", "failed_timeout", "too_many_rows_split_filters"].includes(data.status)) {
        setStarting(false); return;
      }
      await new Promise((r) => setTimeout(r, 500));
    }
    setStarting(false);
  };

  const download = async () => {
    const { data } = await api.get(`/tenant/exports/${job.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = job.artifact_filename || `export-${job.id}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };
  const preflight = async () => {
    const { data } = await api.get(`/tenant/exports/${job.id}/preflight.csv`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = `preflight-${job.id}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const title = preset_meta?.label || profile_meta?.name || "Run export";

  return (
    <div className="space-y-6" data-testid="export-run-page">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/app/admin/exports"><ArrowLeft className="h-3 w-3" /> Back to exports</Link>
        </Button>
        <h1 className="mt-2 font-display text-3xl font-semibold text-primary">{title}</h1>
        {profile_meta?.description && <p className="mt-1 text-muted-foreground">{profile_meta.description}</p>}
      </div>

      {!profileId && (
        <Card>
          <CardHeader><CardTitle className="text-base">Date range</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={dates.from}
                     onChange={(e) => setDates({ ...dates, from: e.target.value })}
                     data-testid="run-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={dates.to}
                     onChange={(e) => setDates({ ...dates, to: e.target.value })}
                     data-testid="run-to" />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={run} disabled={starting} data-testid="run-btn">
          {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Run export
        </Button>
        {progress && (
          <span className="text-sm text-muted-foreground" data-testid="run-progress">
            {progress.processed} / {progress.total || "?"} · {progress.status}
          </span>
        )}
      </div>

      {job && (
        <Card>
          <CardHeader><CardTitle className="text-base">Result</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline">{job.status}</Badge>
              <Badge variant="outline">{job.row_count} rows</Badge>
              {job.artifact_filename && <span className="text-xs text-muted-foreground">{job.artifact_filename}</span>}
            </div>
            {job.preflight_error_count > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm">
                <div className="flex items-center gap-2 font-medium text-amber-800">
                  <AlertCircle className="h-4 w-4" /> {job.preflight_error_count} preflight issue(s)
                </div>
                <p className="mt-1 text-amber-800">
                  Rows are still exported, but the vendor may reject them. Download the preflight CSV
                  to see what to fix (usually missing payroll_employee_id).
                </p>
                <Button size="sm" variant="outline" className="mt-2" onClick={preflight}
                        data-testid="download-preflight-btn">
                  <Download className="h-3 w-3" /> Download preflight.csv
                </Button>
              </div>
            )}
            {job.status === "ready" && (
              <Button onClick={download} data-testid="download-btn">
                <Download className="h-4 w-4" /> Download {job.filetype.toUpperCase()}
              </Button>
            )}
            {(job.warnings || []).length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                   data-testid="run-warnings">
                <div className="font-medium">Warnings</div>
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  {(job.warnings || []).map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            {job.error_message && (
              <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                {job.error_message}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }
function firstOfMonth() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}
